import React, { useState } from 'react';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import css from './VoucherCodeField.module.css';

/**
 * VoucherCodeField — input + validate button for discount codes.
 *
 * @param {Function} props.onApply   — called with discount object on success
 * @param {Function} [props.onRemove] — called when user removes the code
 * @param {number}   [props.orderAmount] — current order total in minor units
 * @param {string}   [props.currency]
 * @param {string}   [props.className]
 */
const VoucherCodeField = ({ onApply, onRemove, orderAmount, currency, className }) => {
  const intl = useIntl();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);

  const handleApply = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/vouchers/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim(), orderAmount, currency }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) {
        throw new Error(data.error || intl.formatMessage({ id: 'VoucherCodeField.invalidCode' }));
      }
      setApplied(data.discount);
      if (onApply) onApply(data.discount);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setApplied(null);
    setCode('');
    setError(null);
    if (onRemove) onRemove();
  };

  if (applied) {
    const pct = applied.type === 'percent' ? ` (${applied.value}% off)` : '';
    return (
      <div className={classNames(css.root, className, css.applied)}>
        <span className={css.appliedText}>
          {intl.formatMessage({ id: 'VoucherCodeField.applied' })} <strong>{applied.code}</strong>{pct}
        </span>
        <button type="button" onClick={handleRemove} className={css.removeBtn}>
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.inputRow}>
        <input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder={intl.formatMessage({ id: 'VoucherCodeField.placeholder' })}
          className={css.input}
          onKeyDown={e => e.key === 'Enter' && handleApply()}
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={loading || !code.trim()}
          className={css.applyBtn}
        >
          {loading
            ? intl.formatMessage({ id: 'VoucherCodeField.validating' })
            : intl.formatMessage({ id: 'VoucherCodeField.applyButton' })}
        </button>
      </div>
      {error && <p className={css.errorMsg}>{error}</p>}
    </div>
  );
};

export default VoucherCodeField;
